import { AuthContext } from 'context';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import changeLogService from 'services/change-log-service';
import InventoryService from 'services/inventory-items-service';
import partCodesService from 'services/part-code-service';

// Material Dashboard React components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Slide from "@mui/material/Slide";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// E4A React components
import Footer from "e4acomponents/Footer";
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";

import ItemFields from '../components/item-fields';
import PartCodeDetails from "../components/partcode-details";

export default function EditInventoryItem() {
    const { id } = useParams();
    const [inventoryItem, setInventoryItem] = useState({
        city: "",
        partCode: "",
        quantityIn: 0,
        location: "",
        locationId: "",
        rack: "",
        shelf: "",
        pricePerUnit: 0,
        identifier: "",
        drawer: ""
    });
    const [selectedPartCode, setSelectedPartCode] = useState(null);
    const [showPartCodeDetails, setShowPartCodeDetails] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();
    const authContext = useContext(AuthContext);

    useEffect(()=> {
        console.log("Inventory Item Data Updated: ", inventoryItem);
    },[inventoryItem]);

    // Fetches the inventory item from the API
    useEffect(() => {
        (async () => {
            const response = await InventoryService.getOneInventoryItem(id);
            const inventoryItemData = response.data.attributes;
            setInventoryItem(inventoryItemData);
            const response2 = await partCodesService.getPartById(inventoryItemData.partCode._id);
            setSelectedPartCode(response2.data.attributes);
        })();
    }, []);

    // Shows the cards after the data is fetched from the API
    useEffect(() => {
        if (selectedPartCode && !showPartCodeDetails) {
            setShowPartCodeDetails(true);
        } else {
            setShowPartCodeDetails(false);
        }
        changeHandler("partCode", selectedPartCode ? selectedPartCode._id : "");
    }, [selectedPartCode]);

    // Refresh the partcodeDetails card if another partcode is selected
    useEffect(() => {
        if (selectedPartCode && !showPartCodeDetails) {
            setTimeout(() => {
                setShowPartCodeDetails(true);
            }, 1000);
        }
    }, [showPartCodeDetails])

    // Change Handler for the inventory item
    const changeHandler = (target, value) => {
        setInventoryItem({
            ...inventoryItem,
            [target]: value
        });
    }

    const batchChangeHandler = (updates) => {
        setInventoryItem({
            ...inventoryItem,
            ...updates
        })
    }

    const validateForm = () => {
        const requiredFields = [
            `city`,
            `location`
        ]

        const newErrors = {};
        console.log("Checking for inventoryItem: ", inventoryItem);
        for (const field of requiredFields) {
            if (!inventoryItem[field]) {
                if (field === "city" && inventoryItem.locationId) continue;
                if (field === "location" && inventoryItem.locationId) continue;
                newErrors[field] = `This ${field} is required.`;
            }
        }

        return newErrors;
    }

    // Submits the updated inventory item and redirects to inventory view
    const submitHandler = async () => {
        // Validate form before submitting
        const formErrors = validateForm();
        if (Object.keys(formErrors).length > 0) {
            console.log("Form has errors:", formErrors);
            setError(formErrors);
            return;
        }
        try {
            const changeLog = {
                data: {
                    attributes: {
                        inventoryItem: id,
                        change: {
                            type: "Updated",
                            description: `Inventory Item updated`,
                            date: new Date(),
                            user: await authContext.getCurrentUser()
                        }
                    }
                }
            }
            await changeLogService.addChange(changeLog);

            const payload = {
                data: {
                    type: "inventoryItem",
                    id: id,
                    attributes: inventoryItem
                }
            }

            const response = await InventoryService.updateInventoryItem(id, payload);
            console.log(response.data.attributes);
            navigate("/inventory-management", {
                state: { value: false, text: "The Item was succesfully updated" },
            });
        } catch (err) {
            console.log(err);
        }
    }

    return (
        <DashboardLayout>
            <DashboardNavbar breadcrumbTitle={inventoryItem.identifier.replace(/-/g, '–')} />
            <MDBox mt={5} mb={9}>
                <Grid container justifyContent="center" spacing={2}>

                    {/* Title */}
                    <Grid item xs={12} lg={12} >
                        <MDBox
                            mt={6}
                            mb={2}
                            textAlign="center"
                        >
                            <MDBox mb={1}>
                                <MDTypography variant="h3" fontWeight="bold">
                                    Edit Inventory Item
                                </MDTypography>
                            </MDBox>
                            <MDTypography variant="h5" fontWeight="regular" color="secondary">
                                Please do not touch the Select Part-Code section unless you want to change the part code.
                            </MDTypography>
                        </MDBox>
                    </Grid>

                    <Slide direction="up" in={true} mountOnEnter unmountOnExit timeout={1000}>
                        <Grid item xs={12} lg={6}>

                            {/* Inventory Item Details */}
                            {inventoryItem._id && (<ItemFields
                                inventoryItem={inventoryItem}
                                changeHandler={changeHandler}
                                batchChangeHandler={batchChangeHandler}
                                submitHandler={submitHandler}
                                error={error}
                                editMode={true}
                            />)}
                        </Grid>
                    </Slide>

                    <Slide direction="up" in={showPartCodeDetails} mountOnEnter unmountOnExit timeout={1000}>
                        <Grid item xs={12} lg={6}>
                            <MDBox mt={2}>

                                {/* Part Code Details */}
                                <PartCodeDetails id={selectedPartCode ? selectedPartCode._id : "67bf63dd94a996ec95b92bf9"} />

                            </MDBox>

                            {/* Location Format Guide */}
                            <MDBox mt={2}>
                                <Card>
                                    <MDBox p={3}>
                                        <MDTypography variant="h6" fontWeight="medium" mb={2}>
                                            Location Format Guide
                                        </MDTypography>
                                        <MDTypography variant="body2" color="text">
                                            <strong>L</strong> represents the location identifier<br /><br />
                                            <strong>R</strong> represents the rack number. This identifier is variable and can change based on the storage plan.<br /><br />
                                            <strong>S</strong> denotes the shelf number, which is organized sequentially from top to bottom, starting with 1 at the top and increasing to 4.<br /><br />
                                            Therefore, <strong>L1 / Rx / Sy</strong> serves as the E4A identifier, specifying the exact location of items stored on each shelf, with Rx indicating the particular rack and Sy specifying the shelf position from top to bottom.
                                        </MDTypography>
                                    </MDBox>
                                </Card>
                            </MDBox>

                        </Grid>
                    </Slide>

                </Grid>
            </MDBox>
            <MDBox>
                <Footer />
            </MDBox>

        </DashboardLayout>
    )
}